/**
 * Hold to read — the touch half of hover.
 *
 * One listener at the document, mounted once. It watches for a finger that stays put, finds the
 * nearest thing under it with something to say, and puts that up in a bubble. Nothing else in the
 * app has to know it exists: it reads the `title` attributes the screens already carry.
 *
 * Three things it has to get right, all of which are the difference between a feature and an
 * irritation:
 *
 *   IT MUST NOT FIRE ON A SCROLL. A phone is scrolled by putting a finger on content and moving
 *   it, which starts exactly like a hold. Ten pixels of travel cancels.
 *
 *   IT MUST NOT ALSO PRESS THE BUTTON. Holding a board cell to find out whose pick it was must not
 *   then enter a pick into it. The `click` that follows the release is swallowed once.
 *
 *   IT MUST NOT STEAL THE NATIVE HOLD where the native hold is the point — a text field, a link
 *   you meant to copy. `hintFor` refuses those; everything else gets its callout suppressed for
 *   the duration, because two menus at once is worse than either.
 *
 * A held element also gets `.held` for the duration, which is how the hover-only controls — the
 * board's `clear this pick` — become reachable without putting an × on all 160 cells of a phone.
 */

import { useEffect, useRef, useState } from 'react';
import { HOLD_MS, hintFor, movedTooFar, placeHint } from './hints.js';

interface Shown {
  text: string;
  left: number;
  top: number;
  above: boolean;
}

export function TouchHints() {
  const [shown, setShown] = useState<Shown | null>(null);
  /** The element being held, so its `.held` can come off again. */
  const held = useRef<HTMLElement | null>(null);
  /*
   * Whether a bubble is up, as a ref rather than read off the state.
   *
   * The listeners must be subscribed ONCE. Depending the effect on `shown` looked harmless and was
   * not: showing a bubble re-ran the effect, whose cleanup takes `.held` back off the element that
   * had just been given it — so the board's `clear this pick` never appeared under a finger and
   * the ring around the held cell flickered off in the same frame it went on.
   */
  const up = useRef(false);

  useEffect(() => {
    let timer: number | undefined;
    let from: { x: number; y: number } | null = null;
    let target: HTMLElement | null = null;
    /** Set the moment a bubble appears; makes the release swallow its click. */
    let fired = false;

    const release = () => {
      if (held.current) held.current.classList.remove('held');
      held.current = null;
    };

    const cancel = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      from = null;
      target = null;
    };

    const hide = () => {
      cancel();
      release();
      up.current = false;
      setShown(null);
    };

    const fire = () => {
      if (!target) return;
      const found = hintFor(target);
      if (!found) return;

      const node = found.node as unknown as HTMLElement;
      const el = node.getBoundingClientRect();
      /*
       * Measured after the fact would need a second frame, and a bubble that jumps once is worse
       * than one placed from an estimate. 260px is the max-width the stylesheet gives it; the
       * height is estimated from the text and corrected by the clamp in `placeHint`.
       */
      const width = Math.min(260, Math.max(120, found.text.length * 7));
      const height = 24 + Math.ceil((found.text.length * 7) / width) * 17;

      const at = placeHint(
        { left: el.left, top: el.top, width: el.width, height: el.height },
        { width, height },
        { width: window.innerWidth, height: window.innerHeight },
      );

      fired = true;
      up.current = true;
      held.current = node;
      node.classList.add('held');
      // A short buzz is the whole reason a hold feels like a gesture rather than a delay.
      navigator.vibrate?.(8);
      setShown({ text: found.text, ...at });
    };

    const onDown = (e: PointerEvent) => {
      // A mouse already has hover. This is only ever the touch path.
      if (e.pointerType === 'mouse') return;
      // A bubble is up: this tap dismisses it and does nothing else.
      if (up.current) { hide(); return; }
      fired = false;
      from = { x: e.clientX, y: e.clientY };
      target = e.target as HTMLElement;
      timer = window.setTimeout(fire, HOLD_MS);
    };

    const onMove = (e: PointerEvent) => {
      if (!from) return;
      if (movedTooFar(from, { x: e.clientX, y: e.clientY })) cancel();
    };

    const onUp = () => {
      cancel();
      // The bubble stays up after the finger leaves — it is being read — but the press that
      // raised it does not also count as a tap on whatever was underneath.
      if (fired) window.addEventListener('click', swallow, { capture: true, once: true });
      fired = false;
    };

    const swallow = (e: Event) => { e.stopPropagation(); e.preventDefault(); };

    /*
     * The platform's own long-press menu. Suppressed only while OUR hold is running, so a hold
     * anywhere else in the app — and every hold on a text field, which `hintFor` refuses — keeps
     * the behaviour the person expects from their phone.
     */
    const onContext = (e: Event) => { if (held.current) e.preventDefault(); };

    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', hide, true);
    document.addEventListener('contextmenu', onContext);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);

    return () => {
      cancel();
      release();
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', hide, true);
      document.removeEventListener('contextmenu', onContext);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, []);

  if (!shown) return null;

  return (
    <div
      className={`hintbubble ${shown.above ? 'above' : ''}`}
      role="tooltip"
      aria-live="polite"
      style={{ left: shown.left, top: shown.top }}
    >
      {shown.text}
    </div>
  );
}
